/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from "react";
import { Mail, Lock } from "lucide-react";
import TextInput from "../ui/TextInput";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { RootState } from "../../redux/app/store";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../ui/Button";
import AuthPageWrapper from "./AuthPageWrapper";
import { loginSchema } from "./auth_schemas";
import { LoginFormInputs } from "./auth_types";
import { login } from "../../redux/features/authSlice";
import { useAppDispatch } from "../../redux/app/hooks";

// ✅ import your role helpers
import {
  isInvestorOrSuper,
  isCourierOrAdmin,
  isSuperAdmin,
} from "../../helpers";

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { authenticatingUser, isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(loginSchema),
  });

  const handleRedirect = React.useCallback(
    (user: any) => {
      if (isInvestorOrSuper(user)) {
        navigate("/dashboard", { replace: true });
      } else if (isCourierOrAdmin(user)) {
        navigate("/order-management/all-orders", { replace: true });
      } else if (isSuperAdmin(user)) {
        navigate("/product-management", { replace: true });
      } else {
        navigate("/settings", { replace: true });
      }
    },
    [navigate]
  );

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data, event) => {
    event?.preventDefault();
    try {
      const loggedInUser = await dispatch(login(data)).unwrap();
      handleRedirect(loggedInUser);
    } catch (err: any) {
      console.error("Login failed:", err);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      handleRedirect(user);
    }
  }, [isAuthenticated, user, handleRedirect]);

  return (
    <AuthPageWrapper title="Welcome back" sub_title="Sign into your account">
      <form
        className="space-y-4 w-full md:w-[450px]"
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextInput
          id="email"
          type="email"
          label="Email Address"
          placeholder="Enter your email"
          icon={<Mail className="w-5 h-5 text-gray-400" />}
          error={errors.email?.message}
          register={register("email")}
        />
        <TextInput
          id="password"
          type="password"
          label="Password"
          placeholder="Enter your password"
          icon={<Lock className="w-5 h-5 text-gray-400" />}
          error={errors.password?.message}
          register={register("password")}
        />

        <div className="text-right text-sm">
          <Link to="/forgot-password" className="text-accent hover:underline">
            Forgot password?
          </Link>
        </div>
        <Button label="Sign In" type="submit" isLoading={authenticatingUser} />
      </form>
    </AuthPageWrapper>
  );
};

export default LoginPage;
